import React, { type ReactNode } from 'react';
import { Field, useFormikContext } from 'formik';
import { Switch, Button, ButtonCondensed } from '@companycam/slab-web';
import styled from 'styled-components';
import { translate } from 'translations/i18n';
import { tokens } from '@companycam/slab-tokens';

import type { ProjectDatabaseId } from 'data/v1';
import {
  useGetShowcaseProjectProjectTypes,
  useGetShowcaseProjectMaterialsUsed,
} from 'javascripts/data/v3/showcases';

import Header from './Header';
import Footer from './Footer';
import InputErrorMessage from './InputErrorMessage';
import TagSelect from './TagSelect';

import { PreviewCard } from './PreviewCard';
import { useSuggestedTitle } from './useSuggestedTitle';
import { VISIBLE, WizardView } from '../constants';

import type { ConfirmOptions } from './confirm/ConfirmRoot';
import type { ShowcaseWizardFormData } from './Root';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  gap: var(--cc_size_spacing_xl);
  padding: var(--cc_size_spacing_m) 0 var(--cc_size_spacing_xxl);

  @media screen and (min-width: 1024px) {
    flex-direction: row;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 0 var(--cc_size_spacing_m);
`;

const FormContainer = styled.div`
  flex: 1;
`;

const InputContainer = styled.div`
  margin-bottom: ${tokens.size_spacing_l}px;
  width: 100%;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InputLabel = styled.label`
  margin-bottom: ${tokens.size_spacing_l}px;

  label {
    font-size: ${tokens.size_text_s}px;

    strong {
      color: ${tokens.color_text_subtle_le};
      font-weight: 400;
    }
  }
`;

const SuggestedTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin: ${tokens.size_spacing_xs}px 0 ${tokens.size_spacing_m}px;
  font-size: ${tokens.size_text_s}px;

  strong {
    color: ${tokens.color_text_subtle_le};
    font-weight: 400;
  }
`;

const Highlighted = styled.span`
  padding: ${tokens.size_spacing_xs}px ${tokens.size_spacing_s}px;
  margin: 0 ${tokens.size_spacing_s}px 0 ${tokens.size_spacing_xs}px;
  background: rgba(252, 238, 33, 0.35);
  border-radius: ${tokens.size_border_radius_m}px;
`;

const SuggestedTitleLabel = styled.span`
  font-weight: bold;
`;

const VisibilityContainer = styled.div`
  margin-bottom: var(--cc_size_spacing_xl);
`;

const CenterError = styled.div`
  display: flex;
  justify-content: center;
`;

type MainProps = {
  isEditing: boolean;
  setView: (view: WizardView) => void;
  showConfirm: (
    confirmComponentName: ConfirmOptions['componentName'],
    confirmProps: ConfirmOptions['props'],
  ) => void;
  onConfirm: () => void;
  projectDatabaseId: ProjectDatabaseId;
  isSubmitting: boolean;
};

const InputWrapper = ({
  children,
  label,
  htmlFor,
  errorName,
}: {
  children: ReactNode;
  label: string;
  htmlFor: string;
  errorName?: 'materials' | 'title' | 'types';
}) => {
  return (
    <InputContainer>
      <InputLabel>
        <label htmlFor={htmlFor}>{label}</label>
        {children}
        {errorName ? <InputErrorMessage name={errorName} /> : null}
      </InputLabel>
    </InputContainer>
  );
};

const Main = ({
  isEditing,
  setView,
  showConfirm,
  onConfirm,
  projectDatabaseId,
  isSubmitting,
}: MainProps) => {
  const {
    values: { title, materials, types, visible, assets },
    setFieldValue,
    setFieldTouched,
    isValid,
  } = useFormikContext<ShowcaseWizardFormData>();

  const {
    data: showcaseProjectTypes,
    isLoading: isShowcaseProjectTypesLoading,
    isError: isShowcaseProjectTypesError,
  } = useGetShowcaseProjectProjectTypes(projectDatabaseId);

  const {
    data: showcaseProjectMaterials,
    isLoading: isShowcaseProjectMaterialsLoading,
    isError: isShowcaseProjectMaterialsError,
  } = useGetShowcaseProjectMaterialsUsed(projectDatabaseId);

  const titleSuggestion = useSuggestedTitle({ types, materials });

  const handleTitleSuggestionSelect = () => {
    setFieldValue('title', titleSuggestion);
  };

  const renderForm = () => {
    return (
      <FormContainer>
        {isEditing ? (
          <VisibilityContainer>
            <Switch
              id={VISIBLE}
              name={VISIBLE}
              pressed={visible}
              label={translate(
                'showcases.create.v2PublishWizard.main.visbilityLabel',
              )}
              onClick={(e) => {
                e.preventDefault();
                setFieldValue(VISIBLE, !visible);
              }}
            />
          </VisibilityContainer>
        ) : null}
        <InputWrapper
          htmlFor="types"
          label={translate('showcases.create.v2PublishWizard.main.typeLabel')}
          errorName="types"
        >
          <TagSelect
            existingTags={types}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            tagType="types"
            options={showcaseProjectTypes?.data.projectTypes || []}
            isLoading={isShowcaseProjectTypesLoading}
            noOptionsAvailableMessage={translate(
              'showcases.create.publishWizard.main.types.emptyState',
            )}
            error={
              isShowcaseProjectTypesError
                ? translate('showcases.create.publishWizard.main.tags.error')
                : null
            }
          />
        </InputWrapper>
        <InputWrapper
          htmlFor="productsUsed"
          label={translate(
            'showcases.create.v2PublishWizard.main.productsUsedLabel',
          )}
          errorName="materials"
        >
          <TagSelect
            existingTags={materials}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            tagType="materials"
            options={showcaseProjectMaterials?.data.materialsUsed || []}
            isLoading={isShowcaseProjectMaterialsLoading}
            noOptionsAvailableMessage={translate(
              'showcases.create.publishWizard.main.materials.emptyState',
            )}
            error={
              isShowcaseProjectMaterialsError
                ? translate('showcases.create.publishWizard.main.tags.error')
                : null
            }
          />
        </InputWrapper>
        <InputWrapper
          htmlFor="title"
          label={translate('common.title')}
          errorName="title"
        >
          <Field id="title" name="title" type="text" />
          {titleSuggestion && !title ? (
            <SuggestedTitleContainer>
              <SuggestedTitleLabel>
                {translate(
                  'showcases.create.v2PublishWizard.main.suggestedTitleLabel',
                )}
              </SuggestedTitleLabel>
              <Highlighted>{titleSuggestion}</Highlighted>
              <ButtonCondensed
                color="primary"
                size="small"
                aria-label={translate(
                  'showcases.create.v2PublishWizard.main.suggestedTitleButton',
                )}
                onClick={handleTitleSuggestionSelect}
              >
                {translate(
                  'showcases.create.v2PublishWizard.main.suggestedTitleButton',
                )}
              </ButtonCondensed>
            </SuggestedTitleContainer>
          ) : null}
        </InputWrapper>
        <InputWrapper
          htmlFor="description"
          label={translate(
            'showcases.create.v2PublishWizard.main.descriptionLabel',
          )}
        >
          <Field
            component="textarea"
            id="description"
            name="description"
            className="form-field"
            rows="4"
          />
        </InputWrapper>
      </FormContainer>
    );
  };

  const heading = isEditing
    ? translate('showcases.create.v2PublishWizard.main.title.edit')
    : translate('showcases.create.v2PublishWizard.main.title.create');
  const buttonLabel = isEditing
    ? translate('common.update')
    : translate('common.create');
  const description = (
    <>
      {translate('showcase.create.v2PublishWizard.main.description1')}
      <a
        className="inline-link"
        href="https://help.companycam.com/en/articles/6828410-create-and-publish-a-showcase-for-your-website"
        target="_blank"
        rel="noopener noreferrer"
      >
        {translate('showcase.create.v2PublishWizard.main.description2')}
      </a>
      {translate('showcase.create.v2PublishWizard.main.description3')}
    </>
  );

  return (
    <>
      <Header title={heading} description={description} hideModal={onConfirm} />
      <Container>
        {renderForm()}
        <PreviewContainer>
          <PreviewCard
            setView={setView}
            coverPhotoButtonVisible={assets.length > 0}
            thumbnails={assets.slice(0, 5)}
          />
          <ButtonCondensed
            color="primary"
            style={{ margin: 'var(--cc_size_spacing_s) auto 0px' }}
            onClick={() => setView('SELECT_PHOTOS')}
          >
            Select Photos
          </ButtonCondensed>
          <CenterError>
            <InputErrorMessage name="assets" />
          </CenterError>
        </PreviewContainer>
      </Container>
      <Footer>
        <Button
          color="primary"
          className={!isValid ? 'hint--top-center' : ''}
          style={{ marginLeft: 'auto' }}
          type="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {buttonLabel}
        </Button>
        {isEditing ? (
          <Button
            color="destroy"
            design="minimal"
            onClick={() =>
              showConfirm('DELETE', {
                projectDatabaseId,
              })
            }
          >
            {translate('showcases.publishDropdown.main.deleteConfirmButton')}
          </Button>
        ) : null}
      </Footer>
    </>
  );
};

export default Main;
